/**
 * Create Rule Popover Component
 *
 * Popover that appears when clicking "Create New Rule" button.
 * Similar to GenerateDocumentAction popover pattern.
 */

import React, { useState } from 'react';
import { Box, Button, Popover, TextField, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

interface CreateRulePopoverProps {
  /** Whether the popover is open */
  open: boolean;

  /** Anchor element for the popover */
  anchorEl: HTMLElement | null;

  /** Callback when popover should close */
  onClose: () => void;

  /** Callback when user submits the rule description */
  onSubmit: (description: string) => void;
}

/**
 * Create Rule Popover - prompts user for rule description
 */
export const CreateRulePopover: React.FC<CreateRulePopoverProps> = ({
  open,
  anchorEl,
  onClose,
  onSubmit,
}) => {
  const theme = useTheme();
  const [ruleDescription, setRuleDescription] = useState('');

  const handleClose = () => {
    setRuleDescription('');
    onClose();
  };

  const handleSubmit = () => {
    if (ruleDescription.trim()) {
      onSubmit(ruleDescription.trim());
      handleClose();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      slotProps={{
        paper: {
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.15))',
            mt: -1.5,
            '&::before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              bottom: -10,
              left: '50%',
              width: 20,
              height: 20,
              bgcolor: 'background.paper',
              transform: 'translateX(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        },
      }}
    >
      <Box
        sx={{
          p: 2.5,
          width: 420,
          position: 'relative',
          bgcolor: 'background.paper',
          borderRadius: 1,
        }}
      >
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          What is the rule you want to create?
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Describe the compliance rule that you need and the AI will help you create it.
        </Typography>

        <TextField
          fullWidth
          multiline
          rows={3}
          variant="outlined"
          autoFocus
          placeholder="e.g., Require time documentation for E/M codes, Check for medical necessity criteria..."
          value={ruleDescription}
          onChange={e => setRuleDescription(e.target.value)}
          onKeyDown={handleKeyDown}
          sx={{ mb: 2 }}
        />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button size="small" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleSubmit}
            disabled={!ruleDescription.trim()}
            sx={{ minWidth: 80 }}
          >
            Create
          </Button>
        </Box>
      </Box>
    </Popover>
  );
};

export default CreateRulePopover;

